import { apiConditional, apiUrl } from "@api";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { Query, QueryKey } from "@tanstack/react-query";
import { dehydrate, hydrate } from "@tanstack/react-query";
import type { PersistedClient } from "@tanstack/react-query-persist-client";
import { queryClient } from "@provider/query-client";
import storage from "@provider/storage";

// Catalogus (producten + categorieën) lokaal bewaren in MMKV (op web is dat
// localStorage), zodat een terugkerende bezoeker meteen producten ziet. Daarna
// valideert de app met If-None-Match; meestal is het antwoord een 304.
//
// Bewust synchroon hersteld bij het laden van deze module (MMKV/localStorage
// zijn synchroon) en niet via PersistQueryClientProvider: die herstelt async,
// waardoor de prefetch eerst een volle download zou doen.

// Ophogen als Product/categorie of deze opslag incompatibel verandert.
const CATALOG_CACHE_VERSION = "1";
// API-url erbij: een dev-build tegen de proxy mag geen prod-cache lezen.
const BUSTER = `${CATALOG_CACHE_VERSION}|${apiUrl}`;
const STORAGE_KEY = "catalog-cache";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const PERSIST_THROTTLE_MS = 1000;

const CATALOG_KEYS = ["products", "categories"] as const;
export type CatalogKey = (typeof CATALOG_KEYS)[number];

interface PersistedCatalog extends PersistedClient {
  // ETag per query, hoort bij de data in clientState
  etags: Partial<Record<CatalogKey, string>>;
}

// ETag van de data die nu in de cache staat.
const committedEtags = new Map<CatalogKey, string>();
// ETag van de laatste fetch; pas 'committed' zodra zijn data in de cache staat
// (success-event), zodat opgeslagen data en ETag nooit uit de pas lopen.
const pendingEtags = new Map<CatalogKey, string | undefined>();

// queryFn voor een catalogusquery: conditionele GET met de ETag van de data in
// de cache. Bij 304 blijft die data (inclusief bijgewerkte voorraad) staan.
export async function fetchCatalog<T>(key: CatalogKey, path: string): Promise<T> {
  const cachedData = queryClient.getQueryData<T>([key]);
  const etag = committedEtags.get(key);
  const cached = etag && cachedData !== undefined ? { etag, data: cachedData } : undefined;
  const result = await apiConditional<T>({ path, cached });
  pendingEtags.set(key, result.etag);
  return result.data;
}

function catalogKey(queryKey: QueryKey): CatalogKey | undefined {
  const [first] = queryKey;
  return queryKey.length === 1 ? CATALOG_KEYS.find((key) => key === first) : undefined;
}

function isPersistedQuery(query: Query): boolean {
  return catalogKey(query.queryKey) !== undefined && query.state.status === "success";
}

function commitEtag(key: CatalogKey) {
  const etag = pendingEtags.get(key);
  pendingEtags.delete(key);
  if (etag) {
    committedEtags.set(key, etag);
  } else {
    committedEtags.delete(key);
  }
}

function removeSilently(key: string) {
  try {
    storage.remove(key);
  } catch {
    // opslag niet beschikbaar (privévenster): dan maar zonder cache
  }
}

const persister = createSyncStoragePersister({
  key: STORAGE_KEY,
  throttleTime: PERSIST_THROTTLE_MS,
  storage: {
    getItem: (key) => storage.getString(key) ?? null,
    setItem: (key, value) => {
      try {
        storage.set(key, value);
      } catch {
        // Quota vol (~5 MB localStorage): liever geen cache dan een oude die
        // bij elke start opnieuw geladen wordt.
        removeSilently(key);
      }
    },
    removeItem: removeSilently,
  },
});

function persistCatalog() {
  const persisted: PersistedCatalog = {
    buster: BUSTER,
    timestamp: Date.now(),
    clientState: dehydrate(queryClient, { shouldDehydrateQuery: isPersistedQuery }),
    etags: Object.fromEntries(committedEtags),
  };
  persister.persistClient(persisted);
}

function readPersistedCatalog(): PersistedCatalog | undefined {
  try {
    const raw = storage.getString(STORAGE_KEY);
    if (!raw) return undefined;
    const persisted = JSON.parse(raw) as PersistedCatalog;
    if (persisted.buster === BUSTER && Date.now() - persisted.timestamp <= MAX_AGE) {
      return persisted;
    }
  } catch {
    // kapotte of onleesbare cache: weggooien
  }
  removeSilently(STORAGE_KEY);
  return undefined;
}

function restoreCatalog() {
  const persisted = readPersistedCatalog();
  if (!persisted) return;
  hydrate(queryClient, persisted.clientState);
  for (const key of CATALOG_KEYS) {
    const etag = persisted.etags?.[key];
    if (etag && queryClient.getQueryData([key]) !== undefined) {
      committedEtags.set(key, etag);
    }
  }
}

// Alleen opslaan als de catalogusdata verandert: niet bij elk fetch-statusje
// en niet bij andere queries (de voorraadpoll loopt elke 30 s).
function subscribeCatalog() {
  queryClient.getQueryCache().subscribe((event) => {
    const key = catalogKey(event.query.queryKey);
    if (!key) return;
    if (event.type === "updated" && event.action.type === "success") {
      // setQueryData (voorraad) is 'manual': die hoort niet bij een fetch
      if (!event.action.manual) {
        commitEtag(key);
      }
      persistCatalog();
    } else if (event.type === "removed") {
      persistCatalog();
    }
  });
}

restoreCatalog();
subscribeCatalog();
